import { getToken } from './tokenStore';
import axios from '../config/axios';
import API_ENDPOINTS from '../config/api';
import { cptHtmlToPlainText } from '../utils/cptTemplateHtml';
import { TEMPLATE_INTAKE_FIELD_META } from '../data/templateIntakeCheckboxFieldMap';
import {
  ALL_TEMPLATE_LANGUAGE_FIELD_KEYS,
  DISCONTINUATION_TEMPLATE_FIELD_KEYS,
} from '../constants/templateLanguageFieldRows';

/** Section 11 template-checkbox fields in regulatory_requirements (not comments). */
const REGULATORY_TEMPLATE_FIELD_KEYS = Object.keys(TEMPLATE_INTAKE_FIELD_META).filter(
  (key) =>
    !ALL_TEMPLATE_LANGUAGE_FIELD_KEYS.includes(key) &&
    !DISCONTINUATION_TEMPLATE_FIELD_KEYS.includes(key)
);

const DISCONTINUATION_TEMPLATE_LLM_EXCLUDE_KEYS = [...DISCONTINUATION_TEMPLATE_FIELD_KEYS];

const omitTemplateCheckboxFields = (section, excludeKeys) => {
  if (!section || typeof section !== 'object' || !excludeKeys?.length) return section;
  return Object.fromEntries(
    Object.entries(section).filter(([key]) => !excludeKeys.includes(key))
  );
};

// Function to fetch latest clinical intake data from MongoDB for a specific project
export const fetchLatestClinicalIntakeData = async (projectId) => {
  try {
    if (!projectId) {
      console.error('fetchLatestClinicalIntakeData requires projectId — refusing global/latest-across-studies fetch');
      throw new Error('projectId is required to fetch clinical intake for the current study');
    }
    console.log('Fetching clinical intake data from:', API_ENDPOINTS.clinicalIntake.latest, 'projectId:', projectId);
    const response = await axios.get(API_ENDPOINTS.clinicalIntake.latest, { params: { projectId } });
    
    if (!response.data) {
      console.error('No data received from clinical intake API');
      return null;
    }
    
    if (!response.data.success) {
      console.error('API returned unsuccessful response:', response.data);
      return null;
    }
    
    // Handle case where no form exists yet (data is null but success is true)
    if (response.data.data === null) {
      console.log('No clinical intake form exists for this project yet');
      return null;
    }
    
    console.log('Successfully fetched clinical intake data:', response.data.data);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching clinical intake data:', error.response?.data || error.message);
    return null;
  }
};

// Function to check if clinical intake form is submitted (locked)
export const isClinicalIntakeSubmitted = async (projectId) => {
  try {
    const data = await fetchLatestClinicalIntakeData(projectId);
    if (!data) return false;
    
    // Only lock the form if it has an explicit 'submitted' status
    // Don't lock based on just having data (could be a draft)
    const hasSubmittedForm = data.status === 'submitted';
    
    // Check if workflow is complete (stored in database)
    const workflowComplete = data.workflowComplete === true || data.documentEditorVisited === true;
    
    // Check if workflow is complete (stored in localStorage as fallback)
    const localStorageWorkflowComplete = localStorage.getItem(`clinicalIntakeWorkflowComplete_${projectId}`) === 'true';
    const localStorageDocumentEditorVisited = localStorage.getItem(`documentEditorVisited_${projectId}`) === 'true';
    
    // Form should be locked ONLY if it was explicitly submitted OR if workflow is complete
    const shouldLock = hasSubmittedForm || workflowComplete || localStorageWorkflowComplete || localStorageDocumentEditorVisited;
    
    console.log('Lock detection for project', projectId, ':', {
      hasSubmittedForm,
      workflowComplete,
      localStorageWorkflowComplete,
      localStorageDocumentEditorVisited,
      shouldLock,
      data: {
        status: data.status,
        submitted: data.submitted,
        submittedAt: data.submittedAt,
        _id: data._id,
        workflowComplete: data.workflowComplete,
        documentEditorVisited: data.documentEditorVisited
      }
    });
    
    return shouldLock;
  } catch (error) {
    console.error('Error checking clinical intake submission status:', error);
    return false;
  }
};

// Function to format clinical intake data for LLM context
export const formatClinicalIntakeForLLM = (data, options = {}) => {
  if (!data) return '';

  const { forTemplateGeneration = false } = options;

  const formatValue = (value) => {
    if (Array.isArray(value)) {
      return value
        .map((v) => {
          if (typeof v === 'string') return cptHtmlToPlainText(v);
          if (v && typeof v === 'object') return JSON.stringify(v);
          return v;
        })
        .join(', ');
    }
    if (typeof value === 'string') return cptHtmlToPlainText(value);
    if (value && typeof value === 'object') return JSON.stringify(value);
    return value;
  };

  const formatSection = (section, title) => {
    if (!section) return '';
    return `${title}:\n${Object.entries(section)
      .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
      .map(([key, value]) => `${key}: ${formatValue(value)}`)
      .join('\n')}\n\n`;
  };

  const formatNestedSection = (section, title) => {
    if (!section) return '';
    let result = `${title}:\n`;
    
    Object.entries(section).forEach(([key, value]) => {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        result += formatSection(value, key);
      } else if (value !== '' && value !== null && value !== undefined) {
        result += `${key}: ${formatValue(value)}\n`;
      }
    });
    
    return result + '\n';
  };

  const formatAppendices = (appendices) => {
    if (!Array.isArray(appendices) || appendices.length === 0) return '';
    const lines = appendices
      .map((a) => {
        if (!a) return '';
        if (typeof a === 'string') return a;
        if (typeof a === 'object') {
          const title = (a.title || '').toString().trim();
          const desc = (a.description || '').toString().trim();
          const html = (a.contentHtml || '').toString().trim();
          if (!title && !desc && !html) return '';
          let line = title && desc ? `${title}: ${desc}` : title || desc;
          if (html) {
            line = `${line || title || 'Appendix'}\nUploaded appendix body (authoritative HTML):\n${html}`;
          }
          return line;
        }
        return String(a);
      })
      .filter(Boolean);
    return lines.length ? `Appendices:\n${lines.join('\n')}\n\n` : '';
  };

  let context = '';

  // Format each section
  context += formatSection(data.study_identification, 'Study Identification');
  context += formatSection(data.study_overview, 'Study Overview');
  context += formatSection(data.endpoints_objectives, 'Endpoints and Objectives');
  context += formatSection(data.target_population, 'Target Population');
  context += formatSection(data.study_treatments, 'Study Treatments');
  context += formatNestedSection(
    forTemplateGeneration
      ? omitTemplateCheckboxFields(data.discontinuation_rules, DISCONTINUATION_TEMPLATE_LLM_EXCLUDE_KEYS)
      : data.discontinuation_rules,
    'Discontinuation Rules'
  );
  context += formatNestedSection(data.study_assessments, 'Study Assessments');
  context += formatSection(data.statistical_considerations, 'Statistical Considerations');
  context += formatSection(
    forTemplateGeneration
      ? omitTemplateCheckboxFields(data.regulatory_requirements, REGULATORY_TEMPLATE_FIELD_KEYS)
      : data.regulatory_requirements,
    'Regulatory Requirements'
  );

  // Never feed document-parsed / saved template language back into template generation —
  // it causes the model to echo upload text instead of CPT standard language.
  if (!forTemplateGeneration) {
    context += formatNestedSection(data.template_language_fields, 'Template Language Fields (Saved / Generated)');
  }


  context += formatAppendices(data.appendices);


  if (data.additional_comments) {
    context += `Additional Comments:\n${data.additional_comments}\n\n`;
  }

  return context.trim();
};

// Function to get latest clinical intake data and format it for LLM
export const getLatestClinicalIntakeContext = async (projectId) => {
  try {
    const latestData = await fetchLatestClinicalIntakeData(projectId);
    return formatClinicalIntakeForLLM(latestData);
  } catch (error) {
    console.error('Error getting clinical intake context:', error);
    return '';
  }
};

// Function to get latest clinical intake data for a project
const getLatestClinicalIntake = async (projectId) => {
  try {
    if (!projectId) {
      throw new Error('projectId is required to fetch clinical intake for the current study');
    }
    const response = await axios.get(`${API_ENDPOINTS.clinicalIntake.latest}`, { params: { projectId } });
    return response.data.data;
  } catch (error) {
    if (error.response && error.response.status === 404 && error.response.data.message === 'No intake form for current project') {
      throw new Error('No intake form for current project');
    }
    console.error('Error fetching latest clinical intake:', error);
    throw error;
  }
};

// Get all forms for a project
const getProjectForms = async (projectId) => {
  try {
    const response = await axios.get(`${API_ENDPOINTS.clinicalIntake.list}`, { params: { projectId } });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Get a single form
const getForm = async (formId, projectId) => {
  try {
    const response = await axios.get(API_ENDPOINTS.clinicalIntake.get(formId), { params: { projectId } });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Create a new form
const createForm = async (formData, projectId) => {
  try {
    const response = await axios.post(API_ENDPOINTS.clinicalIntake.submit, { ...formData, projectId });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Update an existing form
const updateForm = async (formId, formData, projectId) => {
  try {
    const response = await axios.put(API_ENDPOINTS.clinicalIntake.get(formId), { ...formData, projectId });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Delete a form
const deleteForm = async (formId, projectId) => {
  try {
    const response = await axios.delete(API_ENDPOINTS.clinicalIntake.get(formId), { data: { projectId } });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Get form history
const getFormHistory = async (formId, projectId) => {
  try {
    const response = await axios.get(`${API_ENDPOINTS.clinicalIntake.get(formId)}/history`, { params: { projectId } });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Upload files for a form
const uploadFiles = async (formId, files, projectId) => {
  try {
    const formData = new FormData();
    Object.entries(files).forEach(([key, file]) => {
      formData.append(key, file);
    });
    formData.append('projectId', projectId);

    const response = await axios.post(
      `${API_ENDPOINTS.clinicalIntake.get(formId)}/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Create or update a form (draft or submitted — preserves status when provided)
const saveDraft = async (formData, { status: statusOverride, lockForm = false } = {}) => {
  try {
    const {
      id,
      projectId,
      status: formStatus,
      lockForm: _lf,
      isLocked: _locked,
      lockedAt: _lockedAt,
      lockedVersion: _lockedVersion,
      unlockedAt: _unlockedAt,
      unlockReason: _unlockReason,
      ...data
    } = formData;

    if (!projectId) {
      throw new Error('Project ID is required to save a draft.');
    }

    const saveStatus = statusOverride || formStatus || 'draft';
    const payload = { ...data, projectId, status: saveStatus, lockForm };

    let response;
    if (id) {
      response = await axios.put(API_ENDPOINTS.clinicalIntake.get(id), payload);
    } else {
      response = await axios.post(API_ENDPOINTS.clinicalIntake.submit, payload);
    }
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Fetch latest clinical intake data for a project and ensure it includes a trial_schema file
const fetchLatestClinicalIntakeWithTrialSchema = async (projectId) => {
  try {
    const data = await fetchLatestClinicalIntakeData(projectId);
    const trialSchemaFiles = data?.document_uploads?.uploaded_files?.trial_schema || [];
    if (trialSchemaFiles.length > 0 && (trialSchemaFiles[0].azureBlobUrl || trialSchemaFiles[0].fileUrl)) {
      return data;
    }
    return null; // No trial schema image found
  } catch (err) {
    return null;
  }
};

// Fetch latest clinical intake with trial schema image for a project from the API
const fetchLatestClinicalIntakeWithTrialSchemaFromApi = async (projectId) => {
  try {
    const response = await axios.get(API_ENDPOINTS.clinicalIntake.latestWithTrialSchema, {
      params: { projectId }
    });
    if (response.data.success) {
      return response.data.data;
    }
    return null;
  } catch (err) {
    console.error('Error fetching trial schema:', err);
    return null;
  }
};

// Function to mark clinical intake workflow as complete (when user goes to document editor)
export const markClinicalIntakeWorkflowComplete = async (projectId) => {
  try {
    // Call the backend endpoint to mark workflow as complete
    const response = await axios.post(API_ENDPOINTS.clinicalIntake.markWorkflowComplete, {
      projectId
    });
    
    if (response.data.success) {
      console.log('Workflow completion marked in backend for project:', projectId);
      return response.data;
    } else {
      throw new Error('Failed to mark workflow complete');
    }
  } catch (error) {
    console.error('Error marking workflow complete:', error);
    
    // Fallback to localStorage if backend fails
    console.log('Falling back to localStorage for project:', projectId);
    localStorage.setItem(`clinicalIntakeWorkflowComplete_${projectId}`, 'true');
    localStorage.setItem(`documentEditorVisited_${projectId}`, 'true');
    
    return { success: true, fallback: true };
  }
};

/** Promote ICF/SAP/Protocol status from not_initiated → draft when work has started. */
export const markDocumentInitiated = async (projectId, documentType) => {
  try {
    const response = await axios.post(API_ENDPOINTS.clinicalIntake.markDocumentInitiated, {
      projectId,
      documentType,
    });
    return response.data;
  } catch (error) {
    console.warn('markDocumentInitiated failed:', error?.response?.data || error.message);
    return null;
  }
};

// Function to unlock clinical intake form
export const unlockClinicalIntake = async (projectId, reason = null) => {
  try {
    const response = await axios.put(API_ENDPOINTS.clinicalIntake.unlock(projectId), {
      reason
    }, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`
      }
    });
    
    if (response.data.success) {
      console.log('Clinical intake form unlocked for project:', projectId);
      return response.data;
    } else {
      throw new Error('Failed to unlock form');
    }
  } catch (error) {
    console.error('Error unlocking clinical intake form:', error);
    throw error;
  }
};

// Function to lock clinical intake form
export const lockClinicalIntake = async (projectId) => {
  try {
    const response = await axios.put(
      API_ENDPOINTS.clinicalIntake.lock(projectId),
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );

    if (response.data.success) {
      console.log('Clinical intake form locked for project:', projectId);
      return response.data;
    }
    throw new Error(response.data.message || 'Failed to lock form');
  } catch (error) {
    console.error('Error locking clinical intake form:', error);
    throw error.response?.data || error;
  }
};

// Function to detect changes in clinical intake form
export const detectClinicalIntakeChanges = async (projectId) => {
  try {
    // Add cache-busting param to avoid stale 304 responses
    const url = `${API_ENDPOINTS.clinicalIntake.detectChanges(projectId)}?t=${Date.now()}`;
    const response = await axios.get(url, {
      headers: {
        'Authorization': `Bearer ${getToken()}`
      }
    });
    
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to detect changes');
    }
  } catch (error) {
    console.error('Error detecting changes:', error);
    // Return no changes if error occurs
    return { hasChanges: false, changes: [] };
  }
};

// Function to acknowledge changes (update lockedVersion after regeneration)
export const acknowledgeClinicalIntakeChanges = async (projectId) => {
  try {
    const response = await axios.post(API_ENDPOINTS.clinicalIntake.acknowledgeChanges(projectId), {}, {
      headers: {
        'Authorization': `Bearer ${getToken()}`
      }
    });
    
    if (response.data.success) {
      return response.data;
    } else {
      throw new Error('Failed to acknowledge changes');
    }
  } catch (error) {
    console.error('Error acknowledging changes:', error);
    throw error;
  }
};



// Export all functions
export default {
  fetchLatestClinicalIntakeData,
  formatClinicalIntakeForLLM,
  getLatestClinicalIntakeContext,
  getLatestClinicalIntake,
  getProjectForms,
  getForm,
  createForm,
  updateForm,
  deleteForm,
  getFormHistory,
  uploadFiles,
  saveDraft,
  fetchLatestClinicalIntakeWithTrialSchema,
  fetchLatestClinicalIntakeWithTrialSchemaFromApi,
  isClinicalIntakeSubmitted,
  markClinicalIntakeWorkflowComplete,
  markDocumentInitiated,
  unlockClinicalIntake,
  lockClinicalIntake,
  detectClinicalIntakeChanges,
  acknowledgeClinicalIntakeChanges
}; 